import { useReducer, useEffect } from 'react';
import { Units } from '../../units/units';
import { Weight } from '../../units/useWeight';
import useGetScaleWeight from '../../../requests/useGetScaleWeight';
import usePollingQuery from './usePollingQuery';
import useTimeout from './useTimeout';

const minimumDisplayTimeMs = 5000;
const escalationTimeMs = 60000;
const weightRefreshTimeMs = 1500;
const defaultWeight: Weight = { value: 0, unit: Units.lb };

interface UseScaleWeightProps {
  siteId: string;
  scaleId: string;
}

interface UseScaleWeightResult {
  weight: Weight;
  isReconnecting: boolean;
  showEscalationMessage: boolean;
}

type ScaleWeightState =
  | { status: 'Connected'; weight: Weight }
  | { status: 'Reconnecting' | 'ReconnectingCanHide' | 'Escalated' };

type ScaleWeightAction =
  | { type: 'ScaleSuccess'; weight: Weight }
  | { type: 'ScaleError' }
  | { type: 'MinimumTimeElapsed' }
  | { type: 'EscalationTimeElapsed' };

function scaleWeightReducer(state: ScaleWeightState, action: ScaleWeightAction): ScaleWeightState {
  switch (action.type) {
    case 'ScaleError':
      if (state.status === 'Connected') {
        return { status: 'Reconnecting' };
      }
      return state;

    case 'ScaleSuccess':
      if (state.status !== 'Connected') {
        if (state.status === 'ReconnectingCanHide' || state.status === 'Escalated') {
          return { status: 'Connected', weight: action.weight };
        }
        return state;
      }
      return { status: 'Connected', weight: action.weight };

    case 'MinimumTimeElapsed':
      if (state.status === 'Reconnecting') {
        return { status: 'ReconnectingCanHide' };
      }
      return state;

    case 'EscalationTimeElapsed':
      if (state.status === 'Reconnecting' || state.status === 'ReconnectingCanHide') {
        return { status: 'Escalated' };
      }
      return state;

    default:
      return state;
  }
}

const useScaleWeight = ({ siteId, scaleId }: UseScaleWeightProps): UseScaleWeightResult => {
  const [state, dispatch] = useReducer(scaleWeightReducer, {
    status: 'Connected',
    weight: defaultWeight
  });

  const minimumTimeout = useTimeout(() => dispatch({ type: 'MinimumTimeElapsed' }), minimumDisplayTimeMs);
  const escalationTimeout = useTimeout(() => dispatch({ type: 'EscalationTimeElapsed' }), escalationTimeMs);

  const getWeight = useGetScaleWeight(siteId, scaleId, {
    onSuccess: weight => dispatch({ type: 'ScaleSuccess', weight: { value: weight.value, unit: weight.unit, } }),
    onError: () => dispatch({ type: 'ScaleError' }),
  });

  usePollingQuery(getWeight, weightRefreshTimeMs);

  useEffect(() => {
    if (state.status === 'Reconnecting') {
      minimumTimeout.start();
      escalationTimeout.start();

    } else if (state.status === 'Connected') {
      minimumTimeout.stop();
      escalationTimeout.stop();
    }
  }, [state.status, minimumTimeout, escalationTimeout]);

  const weight = state.status === 'Connected' ? state.weight : defaultWeight;

  return {
    weight,
    isReconnecting: state.status !== 'Connected',
    showEscalationMessage: state.status === 'Escalated'
  };
};

export default useScaleWeight;
